<script setup lang="ts">
/**
 * Modifier les paramètres d'une surveillance (geste 10, route 5 ;
 * `dialogueParametres` S:5671-5694).
 *
 * Ce que l'utilisateur doit vivre : un champ par seuil réglable du type,
 * nommé et dans son unité ; deux champs, site ouvert et site fermé, quand la
 * surveillance suit les horaires du site (D125). Le nouveau seuil vaut pour
 * la suite. Rien ne part si rien n'a changé ; un champ illisible se dit sous
 * le champ ; la plage est jugée par le serveur, qui la dit s'il refuse.
 *
 * Le corps envoyé porte la map ENTIÈRE que la surveillance porte, dans sa
 * forme (une valeur par état sous profil : la façade refuse une valeur
 * simple, `valeurs_nombres_invalides`), chaque réglage non montré recopié tel
 * quel. Le dialogue n'écrit rien lui-même : il rend le corps par `valider`.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Modification,
    AlarmesV2NombreDuType,
    AlarmesV2ValeurChoisie,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    lireParametres,
    saisiesDeDepart,
    type SaisiesDUnNombre,
} from "@modules/alarmes-v2/utils/ecritures"
import { formaterMesure } from "@modules/alarmes-v2/utils/mesure"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** `valeurs_nombres` de la surveillance, tel que servi. */
        valeurs: Record<string, AlarmesV2ValeurChoisie> | null | undefined
        /** Les nombres du type, pour nommer chaque seuil. */
        nombres: AlarmesV2NombreDuType[]
        /** Le nom de l'équipement et du type, sous le titre. */
        contexte?: string
        enCours?: boolean
        refus?: string
    }>(),
    { contexte: "", enCours: false, refus: "" }
)

const emit = defineEmits<{
    valider: [corps: AlarmesV2Modification]
    fermer: []
}>()

/** Les nombres que l'écran sait nommer et que le type laisse régler. */
const getNombres = computed(() =>
    props.nombres.filter(
        (n): n is AlarmesV2NombreDuType & { cle: string; libelle: string } =>
            typeof n?.cle === "string" &&
            typeof n.libelle === "string" &&
            n.libelle !== "" &&
            n.reglable !== false
    )
)

/** Les saisies de départ, depuis la forme servie. */
const depart = saisiesDeDepart(
    props.valeurs,
    getNombres.value.map((n) => n.cle)
)

/** La forme de chaque nombre : un champ, ou site ouvert et site fermé. */
const formes: Record<string, SaisiesDUnNombre["forme"]> = Object.fromEntries(
    Object.entries(depart).map(([cle, s]) => [cle, s.forme])
)

/** Le texte de chaque champ : `cle`, ou `cle.ouvert` et `cle.ferme`. */
const textes = ref<Record<string, string>>(
    Object.fromEntries(
        Object.entries(depart).flatMap(([cle, s]) =>
            s.forme === "simple"
                ? [[cle, s.valeur]]
                : [
                      [`${cle}.ouvert`, s.ouvert],
                      [`${cle}.ferme`, s.ferme],
                  ]
        )
    )
)

/** Les nombres affichés, ceux que la surveillance porte. */
const getChamps = computed(() =>
    getNombres.value.filter((n) => n.cle in formes)
)

/** Vrai quand un champ suit les horaires du site. */
const getSousProfil = computed(() =>
    Object.values(formes).some((f) => f === "profil")
)

/** Les saisies, dans la forme que `lireParametres` lit. */
function saisiesLues(): Record<string, SaisiesDUnNombre> {
    return Object.fromEntries(
        Object.entries(formes).map(([cle, forme]) => [
            cle,
            forme === "simple"
                ? { forme, valeur: textes.value[cle] ?? "" }
                : {
                      forme,
                      ouvert: textes.value[`${cle}.ouvert`] ?? "",
                      ferme: textes.value[`${cle}.ferme`] ?? "",
                  },
        ])
    )
}

const erreurs = ref<Record<string, string>>({})
const rienAChanger = ref(false)

/** Le libellé d'un champ, avec son unité. */
function libelle(n: AlarmesV2NombreDuType & { libelle: string }): string {
    const unites = MESSAGES_ALARMES_V2.unites as Record<string, string>
    return n.unite && n.unite in unites
        ? t("alarmesV2.actions.parametresDialogue.enUnite", {
              libelle: n.libelle,
              unite: unites[n.unite],
          })
        : n.libelle
}

/** La plage du type, si elle est servie. */
function plage(n: AlarmesV2NombreDuType): string {
    const min = n.plage?.min
    const max = n.plage?.max
    if (typeof min !== "number" || typeof max !== "number") return ""
    return t("alarmesV2.actions.parametresDialogue.plage", {
        min: formaterMesure(min, n.unite, tr),
        max: formaterMesure(max, n.unite, tr),
    })
}

// ================================
// HANDLERS
// ================================
function onEnregistrer(): void {
    if (props.enCours) return
    rienAChanger.value = false
    const lu = lireParametres(props.valeurs ?? {}, saisiesLues(), {
        illisible: t("alarmesV2.actions.parametresDialogue.illisible"),
        vide: t("alarmesV2.actions.parametresDialogue.vide"),
    })
    if (!lu.ok) {
        erreurs.value = lu.erreurs
        return
    }
    erreurs.value = {}
    // Rien n'a changé : aucun corps ne part.
    if (lu.valeurs === null) {
        rienAChanger.value = true
        return
    }
    emit("valider", { valeurs_nombres: lu.valeurs })
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreDialogue
        :titre="t('alarmesV2.actions.parametresDialogue.titre')"
        :bloque="enCours"
        @fermer="emit('fermer')"
    >
        <template #intro>
            <p class="text-sm text-surface-600">
                <template v-if="contexte">{{ contexte }}. </template
                >{{ t("alarmesV2.actions.parametresDialogue.texte") }}
            </p>
        </template>

        <p
            v-if="getChamps.length === 0"
            class="text-sm text-surface-700"
            data-test="aucun-reglable"
        >
            {{ t("alarmesV2.actions.parametresDialogue.aucunReglable") }}
        </p>

        <div
            v-for="n in getChamps"
            :key="n.cle"
            class="flex flex-col gap-1"
            :data-test="`champ-${n.cle}`"
        >
            <template v-if="formes[n.cle] === 'simple'">
                <label class="text-sm" :for="`param-${n.cle}`">{{
                    libelle(n)
                }}</label>
                <input
                    :id="`param-${n.cle}`"
                    v-model="textes[n.cle]"
                    type="text"
                    inputmode="decimal"
                    class="rounded border border-surface-300 px-2 py-1"
                    :aria-invalid="erreurs[n.cle] ? 'true' : undefined"
                    :data-test="`saisie-${n.cle}`"
                />
                <span v-if="erreurs[n.cle]" class="text-xs text-red-800">{{
                    erreurs[n.cle]
                }}</span>
            </template>
            <template v-else>
                <label class="text-sm" :for="`param-${n.cle}-ouvert`">{{
                    t("alarmesV2.actions.parametresDialogue.siteOuvert", {
                        libelle: libelle(n),
                    })
                }}</label>
                <input
                    :id="`param-${n.cle}-ouvert`"
                    v-model="textes[`${n.cle}.ouvert`]"
                    type="text"
                    inputmode="decimal"
                    class="rounded border border-surface-300 px-2 py-1"
                    :aria-invalid="
                        erreurs[`${n.cle}.ouvert`] ? 'true' : undefined
                    "
                    :data-test="`saisie-${n.cle}-ouvert`"
                />
                <span
                    v-if="erreurs[`${n.cle}.ouvert`]"
                    class="text-xs text-red-800"
                    >{{ erreurs[`${n.cle}.ouvert`] }}</span
                >
                <label class="text-sm" :for="`param-${n.cle}-ferme`">{{
                    t("alarmesV2.actions.parametresDialogue.siteFerme", {
                        libelle: libelle(n),
                    })
                }}</label>
                <input
                    :id="`param-${n.cle}-ferme`"
                    v-model="textes[`${n.cle}.ferme`]"
                    type="text"
                    inputmode="decimal"
                    class="rounded border border-surface-300 px-2 py-1"
                    :aria-invalid="
                        erreurs[`${n.cle}.ferme`] ? 'true' : undefined
                    "
                    :data-test="`saisie-${n.cle}-ferme`"
                />
                <span
                    v-if="erreurs[`${n.cle}.ferme`]"
                    class="text-xs text-red-800"
                    >{{ erreurs[`${n.cle}.ferme`] }}</span
                >
            </template>
            <span v-if="plage(n)" class="text-xs text-surface-600">{{
                plage(n)
            }}</span>
        </div>

        <p v-if="getSousProfil" class="text-xs text-surface-600">
            {{ t("alarmesV2.actions.parametresDialogue.horaires") }}
        </p>
        <p
            v-if="rienAChanger"
            class="text-sm text-surface-700"
            role="status"
            data-test="rien-a-changer"
        >
            {{ t("alarmesV2.actions.parametresDialogue.rienAChanger") }}
        </p>
        <p v-if="enCours" class="text-sm text-surface-700" role="status">
            {{ t("alarmesV2.actions.envoi") }}
        </p>
        <p
            v-else-if="refus"
            class="text-sm text-red-800"
            role="alert"
            data-test="refus"
        >
            {{ refus }}
        </p>

        <template #pied>
            <button
                type="button"
                class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                :aria-disabled="enCours ? 'true' : undefined"
                data-focus-initial
                data-test="annuler"
                @click="!enCours && emit('fermer')"
            >
                {{ t("alarmesV2.actions.annuler") }}
            </button>
            <button
                v-if="getChamps.length > 0"
                type="button"
                class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                :aria-disabled="enCours ? 'true' : undefined"
                data-test="enregistrer"
                @click="onEnregistrer"
            >
                {{ t("alarmesV2.actions.enregistrer") }}
            </button>
        </template>
    </CadreDialogue>
</template>
